import { Shell } from "@/components/layout/Shell";
import {
  usePecas,
  usePecaPerguntas,
  usePerguntasPadrao,
  useBancoPerguntas,
} from "@/hooks/use-data";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Loader2,
  Save,
  Settings2,
  Library,
  ClipboardList,
  X,
  Link2,
} from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import type { Peca, BancoPergunta, UfSequencia } from "@shared/schema";
import { VEICULO_TIPO_LABELS, VEICULO_TIPO_COLORS } from "@/lib/constants";

// ─── Editor de perguntas (usado apenas no gerenciamento do banco) ─────────

function findDuplicateIndices(textos: string[]): Set<number> {
  const seen = new Map<string, number>(); // normalizado → primeiro índice
  const dupes = new Set<number>();
  textos.forEach((t, i) => {
    const key = t.trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      dupes.add(seen.get(key)!);
      dupes.add(i);
    } else {
      seen.set(key, i);
    }
  });
  return dupes;
}

function QuestionEditor({
  textos,
  onChange,
}: {
  textos: string[];
  onChange: (textos: string[]) => void;
}) {
  const add = () => onChange([...textos, ""]);
  const remove = (i: number) => onChange(textos.filter((_, idx) => idx !== i));
  const update = (i: number, v: string) =>
    onChange(textos.map((t, idx) => (idx === i ? v : t)));

  const dupes = useMemo(() => findDuplicateIndices(textos), [textos]);

  return (
    <div className="space-y-2">
      {textos.map((texto, i) => {
        const isDupe = dupes.has(i);
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-400 w-5 text-right flex-shrink-0">
              {i + 1}.
            </span>
            <div className="flex-1 relative">
              <Input
                value={texto}
                onChange={(e) => update(i, e.target.value)}
                placeholder={`Pergunta ${i + 1}`}
                className={
                  isDupe
                    ? "border-destructive focus-visible:ring-destructive pr-24"
                    : ""
                }
              />
              {isDupe && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-destructive font-medium pointer-events-none">
                  duplicada
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => remove(i)}
              className="text-destructive hover:text-destructive flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      })}
      <div className="pt-1 text-xs text-muted-foreground flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />
        Campo de <strong>Observação</strong> aparece automaticamente nas
        inspeções.
      </div>
      <Button variant="outline" size="sm" onClick={add} className="mt-1">
        <Plus className="w-4 h-4 mr-1" />
        Adicionar Pergunta
      </Button>
    </div>
  );
}

// ─── Seletor de perguntas do banco (substitui QuestionEditor nas peças) ───

function BancoSelector({
  textos,
  onChange,
  session,
  onSaveWithTextos,
}: {
  textos: string[];
  onChange: (textos: string[]) => void;
  session?: any;
  onSaveWithTextos?: (textos: string[]) => void;
}) {
  const { data: banco, isLoading } = useBancoPerguntas();
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [pendingCreate, setPendingCreate] = useState<string | null>(null);
  const [showBatch, setShowBatch] = useState(false);
  const [batchText, setBatchText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const saveAfterBatchRef = useRef(false);

  // ── Busca individual ──
  const filtered = useMemo(() => {
    if (!banco) return [];
    if (!query.trim()) return banco;
    const q = query.trim().toLowerCase();
    return banco.filter((item: BancoPergunta) =>
      item.texto.toLowerCase().includes(q),
    );
  }, [banco, query]);

  // ── Batch: processamento do texto colado ──
  const batchRaw = useMemo(
    () =>
      batchText
        .split("\n")
        .map((l) => l.replace(/^[☐☑✓✗✘○●•\-\*]\s*/, "").trim())
        .filter(Boolean),
    [batchText],
  );
  const batchIgnoradas = useMemo(
    () =>
      batchRaw.filter((l) => l.toLowerCase().search("outros") !== -1).length,
    [batchRaw],
  );
  const batchProcessed = useMemo(
    () => batchRaw.filter((l) => l.toLowerCase().search("outros") === -1),
    [batchRaw],
  );
  const batchExistentes = useMemo(() => {
    if (!banco) return [];
    const bancoMap = new Map(
      banco.map((b: BancoPergunta) => [b.texto.trim().toLowerCase(), b.texto]),
    );
    return batchProcessed
      .map((l) => bancoMap.get(l.toLowerCase()))
      .filter((t): t is string => t !== undefined);
  }, [batchProcessed, banco]);
  const batchNovas = useMemo(() => {
    if (!banco) return batchProcessed;
    const bancoSet = new Set(
      banco.map((b: BancoPergunta) => b.texto.trim().toLowerCase()),
    );
    return batchProcessed.filter((l) => !bancoSet.has(l.toLowerCase()));
  }, [batchProcessed, banco]);

  const toggle = (texto: string) => {
    if (textos.includes(texto)) {
      onChange(textos.filter((t) => t !== texto));
    } else {
      onChange([...textos, texto]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = query.trim();
    if (!q) return;

    if (filtered.length > 0) {
      const exact = filtered.find(
        (item) => item.texto.trim().toLowerCase() === q.toLowerCase(),
      );
      const target = exact ?? filtered[0];
      if (!textos.includes(target.texto)) {
        onChange([...textos, target.texto]);
      }
      setQuery("");
      inputRef.current?.focus();
    } else {
      setPendingCreate(q);
    }
  };

  const addToBancoMutation = useMutation({
    mutationFn: async (novaTexto: string) => {
      const currentTextos = banco?.map((p: BancoPergunta) => p.texto) ?? [];
      const res = await fetch(api.bancoPerguntas.sync.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ perguntas: [...currentTextos, novaTexto] }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar pergunta");
      }
      return res.json();
    },
    onSuccess: (_data: any, novaTexto: string) => {
      queryClient.invalidateQueries({
        queryKey: [api.bancoPerguntas.list.path],
      });
      onChange([...textos, novaTexto]);
      setQuery("");
      setPendingCreate(null);
      toast({
        title: "Pergunta criada e selecionada",
        description: "Adicionada ao banco global.",
      });
      setTimeout(() => inputRef.current?.focus(), 50);
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const batchMutation = useMutation({
    mutationFn: async (vars: { existentes: string[]; novas: string[] }) => {
      if (vars.novas.length === 0) return null;
      const currentTextos = banco?.map((p: BancoPergunta) => p.texto) ?? [];
      const res = await fetch(api.bancoPerguntas.sync.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ perguntas: [...currentTextos, ...vars.novas] }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar perguntas");
      }
      return res.json();
    },
    onSuccess: (
      _data: any,
      vars: { existentes: string[]; novas: string[] },
    ) => {
      const toSelect = [...vars.existentes, ...vars.novas];
      const newTextos = [...textos];
      for (const t of toSelect) {
        if (!newTextos.includes(t)) newTextos.push(t);
      }
      onChange(newTextos);
      if (vars.novas.length > 0) {
        queryClient.invalidateQueries({
          queryKey: [api.bancoPerguntas.list.path],
        });
      }
      setShowBatch(false);
      setBatchText("");
      toast({
        title: "Importação concluída",
        description: `${toSelect.length} pergunta(s) selecionadas${vars.novas.length > 0 ? `, ${vars.novas.length} criada(s) no banco` : ""}.`,
      });
      if (saveAfterBatchRef.current) {
        saveAfterBatchRef.current = false;
        onSaveWithTextos?.(newTextos);
      } else {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="px-3 pb-2 space-y-2">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar pergunta… (Enter para selecionar)"
          className="h-8 text-sm"
          autoFocus
        />

        {!showBatch ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBatch(true)}
            className="w-full border-dashed"
          >
            <ClipboardList className="w-4 h-4 mr-1" />
            Importar em Lote
          </Button>
        ) : (
          <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">
                Importar em Lote
              </p>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowBatch(false);
                  setBatchText("");
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Textarea
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              placeholder={"☐ Existem trincas?\n☐ Está empenado?\nOutros"}
              rows={5}
              className="font-mono text-sm"
              autoFocus
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {batchProcessed.length > 0 ? (
                  <>
                    {batchExistentes.length > 0 && (
                      <span className="text-green-600 font-medium">
                        {batchExistentes.length} no banco
                      </span>
                    )}
                    {batchNovas.length > 0 && (
                      <span className="text-blue-600 font-medium">
                        {batchExistentes.length > 0 ? " · " : ""}
                        {batchNovas.length} nova(s)
                      </span>
                    )}
                    {batchIgnoradas > 0 && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {batchIgnoradas} ignorada(s)
                      </span>
                    )}
                  </>
                ) : (
                  'Cole as perguntas — ☐ e "Outros" serão removidos'
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    batchMutation.mutate({
                      existentes: batchExistentes,
                      novas: batchNovas,
                    })
                  }
                  disabled={
                    batchProcessed.length === 0 || batchMutation.isPending
                  }
                >
                  {batchMutation.isPending && !saveAfterBatchRef.current ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-1" />
                  )}
                  Selecionar
                </Button>
                {onSaveWithTextos && (
                  <Button
                    size="sm"
                    onClick={() => {
                      saveAfterBatchRef.current = true;
                      batchMutation.mutate({
                        existentes: batchExistentes,
                        novas: batchNovas,
                      });
                    }}
                    disabled={
                      batchProcessed.length === 0 || batchMutation.isPending
                    }
                  >
                    {batchMutation.isPending && saveAfterBatchRef.current ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Salvar e Selecionar
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {!banco || banco.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-4 rounded-lg bg-slate-50 border border-dashed mx-3">
          <Library className="w-4 h-4 flex-shrink-0" />
          <span>
            Banco de perguntas vazio. Cadastre perguntas na aba{" "}
            <strong>Banco de Perguntas</strong> para selecioná-las aqui.
          </span>
        </div>
      ) : (
        <div className="space-y-1">
          {filtered.length === 0 && query.trim() ? (
            <p className="px-3 py-3 text-sm text-center text-muted-foreground">
              Nenhuma pergunta encontrada.{" "}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 text-xs font-mono border">
                Enter
              </kbd>{" "}
              para criar no banco global.
            </p>
          ) : (
            filtered.map((item: BancoPergunta) => {
              const idx = textos.indexOf(item.texto);
              const checked = idx !== -1;
              const num = checked ? idx + 1 : null;
              return (
                <label
                  key={item.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer select-none"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(item.texto)}
                  />
                  <span className="text-xs font-mono text-slate-400 w-4 text-right flex-shrink-0">
                    {num !== null ? `${num}.` : ""}
                  </span>
                  <span className="text-sm text-slate-700 flex-1">
                    {item.texto}
                  </span>
                </label>
              );
            })
          )}
          <div className="pt-1 text-xs text-muted-foreground flex items-center gap-1 px-3">
            <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />
            Campo de <strong>Observação</strong> aparece automaticamente nas
            inspeções.
          </div>
        </div>
      )}

      <AlertDialog
        open={pendingCreate !== null}
        onOpenChange={(o) => !o && setPendingCreate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Criar pergunta no banco?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="font-medium text-slate-800 block mb-1">
                  "{pendingCreate}"
                </span>
                Não existe no banco global. Deseja criar e selecioná-la?
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingCreate(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                pendingCreate && addToBancoMutation.mutate(pendingCreate)
              }
              disabled={addToBancoMutation.isPending}
            >
              {addToBancoMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              )}
              Criar e selecionar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Gerenciamento do banco global de perguntas ───────────────────────────

function BancoGerenciamento({ session }: { session: any }) {
  const { toast } = useToast();
  const { data: banco, isLoading } = useBancoPerguntas();
  const [textos, setTextos] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [batchText, setBatchText] = useState("");

  useEffect(() => {
    if (banco && !initialized) {
      setTextos(banco.map((p) => p.texto));
      setInitialized(true);
    }
  }, [banco, initialized]);

  const batchLines = batchText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const existentes = new Set(textos.map((t) => t.trim().toLowerCase()));
  const novas = batchLines.filter((l) => !existentes.has(l.toLowerCase()));
  const duplicadas = batchLines.length - novas.length;

  const handleBatchImport = () => {
    if (novas.length === 0) {
      toast({
        title: "Nenhuma pergunta nova",
        description: "Todas as linhas já existem no banco.",
        variant: "destructive",
      });
      return;
    }
    setTextos([...textos, ...novas]);
    setBatchText("");
    setShowBatch(false);
    toast({
      title: "Perguntas importadas",
      description:
        duplicadas > 0
          ? `${novas.length} adicionadas ao editor. ${duplicadas} ignoradas (duplicadas). Clique em "Salvar banco" para confirmar.`
          : `${novas.length} perguntas adicionadas ao editor. Clique em "Salvar banco" para confirmar.`,
    });
  };

  const syncMutation = useMutation({
    mutationFn: async (perguntas: string[]) => {
      const res = await fetch(api.bancoPerguntas.sync.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ perguntas }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar banco");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.bancoPerguntas.list.path],
      });
      if (Array.isArray(data)) setTextos(data.map((p: any) => p.texto));
      toast({
        title: "Banco salvo",
        description: "Perguntas do banco atualizadas com sucesso.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    const validas = textos.map((t) => t.trim()).filter(Boolean);
    if (findDuplicateIndices(validas).size > 0) {
      toast({
        title: "Perguntas duplicadas",
        description:
          "Remova ou corrija as perguntas marcadas como duplicadas antes de salvar.",
        variant: "destructive",
      });
      return;
    }
    syncMutation.mutate(validas);
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="text-sm text-muted-foreground">
          Cadastre aqui todas as perguntas que podem aparecer nas inspeções. Ao
          configurar uma peça, você selecionará quais delas se aplicam e em que
          ordem.
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
          </div>
        ) : (
          <>
            <QuestionEditor textos={textos} onChange={setTextos} />

            {/* Import em lote */}
            {showBatch ? (
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    Importar em Lote
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowBatch(false);
                      setBatchText("");
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                  placeholder={
                    "A embalagem está danificada?\nHá sinais de umidade?\nA numeração está legível?"
                  }
                  rows={6}
                  className="font-mono text-sm"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {batchLines.length > 0 && (
                      <>
                        <span className="text-green-600 font-medium">
                          {novas.length} nova(s)
                        </span>
                        {duplicadas > 0 && (
                          <span className="text-muted-foreground">
                            {" "}
                            · {duplicadas} duplicada(s) serão ignoradas
                          </span>
                        )}
                      </>
                    )}
                    {batchLines.length === 0 && "Uma pergunta por linha"}
                  </p>
                  <Button
                    size="sm"
                    onClick={handleBatchImport}
                    disabled={novas.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar ao banco
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBatch(true)}
                className="w-full border-dashed"
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Importar em Lote
              </Button>
            )}

            <div className="flex justify-end pt-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={syncMutation.isPending}
              >
                {syncMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Salvar banco
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Seção: Perguntas Padrão do Tipo de Veículo ───────────────────────────

function PerguntasPadraoSection({
  tipoVeiculo,
  session,
}: {
  tipoVeiculo: string;
  session: any;
}) {
  const { toast } = useToast();
  const { data: padrao, isLoading } = usePerguntasPadrao(tipoVeiculo);
  const [textos, setTextos] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (padrao && !initialized) {
      setTextos(padrao.map((p) => p.texto));
      setInitialized(true);
    }
  }, [padrao, initialized]);

  // Reinicia quando troca de tipo
  useEffect(() => {
    setInitialized(false);
    setTextos([]);
    setOpen(false);
  }, [tipoVeiculo]);

  const syncMutation = useMutation({
    mutationFn: async (perguntas: string[]) => {
      const res = await fetch(api.perguntasPadrao.sync.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ tipoVeiculo, perguntas }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.perguntasPadrao.list.path, tipoVeiculo],
      });
      // Invalida todas as perguntas de peças para que o fallback seja recalculado
      queryClient.invalidateQueries({
        queryKey: [api.pecaPerguntas.list.path],
      });
      if (Array.isArray(data)) setTextos(data.map((p: any) => p.texto));
      toast({
        title: "Perguntas padrão salvas",
        description: "Aplicadas a todas as peças sem configuração específica.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    const validas = textos.map((t) => t.trim()).filter(Boolean);
    syncMutation.mutate(validas);
  };

  const qtd = padrao?.length ?? 0;

  return (
    <Card className="border-2 border-primary/20 shadow-sm mb-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left rounded-xl"
      >
        <Settings2 className="w-5 h-5 text-primary flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800">
            Perguntas Padrão
          </p>
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? "Carregando..."
              : qtd > 0
                ? `${qtd} pergunta${qtd !== 1 ? "s" : ""} — aplicadas a todas as peças sem configuração específica`
                : "Nenhuma pergunta padrão configurada — cada peça usará suas próprias perguntas"}
          </p>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <CardContent className="border-t pt-4 space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : (
            <>
              <BancoSelector
                textos={textos}
                onChange={setTextos}
                session={session}
              />
              <div className="flex justify-end pt-2">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={syncMutation.isPending}
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Salvar Padrão
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Editor de perguntas específicas por peça ─────────────────────────────

type PecaUnica = {
  id: string;
  nome: string;
  tipoVeiculo: string;
  ufs: UfSequencia[];
};

function deduplicarPecas(pecas: Peca[], tipoVeiculo: string): PecaUnica[] {
  const seen = new Set<string>();
  const result: PecaUnica[] = [];
  for (const p of pecas) {
    const ufseq: UfSequencia[] = [];

    for (const uf of p.ufs) {
      const seq = (p.ufSequencias as Record<string, number>)[uf];

      ufseq.push({ uf: uf, sequencia: seq });
    }

    if (p.tipoVeiculo !== tipoVeiculo) continue;
    if (seen.has(p.nome)) continue;
    seen.add(p.nome);
    result.push({
      id: p.id,
      nome: p.nome,
      tipoVeiculo: p.tipoVeiculo,
      ufs: ufseq,
    });
  }
  return result.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function PecaEditor({
  peca,
  session,
  onSaveSuccess,
}: {
  peca: PecaUnica;
  session: any;
  onSaveSuccess?: () => void;
}) {
  const { toast } = useToast();
  const { data: perguntas, isLoading } = usePecaPerguntas(
    peca.nome,
    peca.tipoVeiculo,
  );
  const [textos, setTextos] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (perguntas && !initialized) {
      setTextos(perguntas.map((p) => p.texto));
      setInitialized(true);
    }
  }, [perguntas, initialized]);

  const syncMutation = useMutation({
    mutationFn: async (data: {
      nome: string;
      tipoVeiculo: string;
      perguntas: string[];
    }) => {
      const res = await fetch(api.pecaPerguntas.sync.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar perguntas");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.pecaPerguntas.list.path, peca.nome, peca.tipoVeiculo],
      });
      if (Array.isArray(data)) setTextos(data.map((p: any) => p.texto));
      toast({
        title: "Perguntas salvas",
        description: "Configuração atualizada com sucesso.",
      });
      onSaveSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    syncMutation.mutate({
      nome: peca.nome,
      tipoVeiculo: peca.tipoVeiculo,
      perguntas: textos,
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 border-t">
      <BancoSelector
        textos={textos}
        onChange={setTextos}
        session={session}
        onSaveWithTextos={(newTextos) =>
          syncMutation.mutate({
            nome: peca.nome,
            tipoVeiculo: peca.tipoVeiculo,
            perguntas: newTextos,
          })
        }
      />
      <div className="flex justify-end pt-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1" />
          )}
          Salvar
        </Button>
      </div>
    </div>
  );
}

function PecaRow({
  peca,
  index,
  session,
  qtdPadrao,
}: {
  peca: PecaUnica;
  index: number;
  session: any;
  qtdPadrao: number;
}) {
  const [open, setOpen] = useState(false);
  const { data: perguntas } = usePecaPerguntas(
    open ? peca.nome : undefined,
    open ? peca.tipoVeiculo : undefined,
  );

  // perguntas retorna específicas OU padrão via fallback
  // para saber se são específicas, buscamos mesmo sem abrir (leve)
  const { data: especificas } = usePecaPerguntas(peca.nome, peca.tipoVeiculo);
  const temEspecificas =
    especificas !== undefined &&
    especificas.length > 0 &&
    qtdPadrao !== especificas.length;
  const qtd = open
    ? (perguntas?.length ?? 0)
    : (especificas?.length ?? qtdPadrao);

  return (
    <div
      id={`peca-row-${index}`}
      className="rounded-lg border bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="text-sm font-medium text-slate-700 flex-1">
          {peca.nome}
          <div className="flex-shrink-0"></div>
          {peca.ufs.map((ufseq, i) => (
            <Badge
              variant="outline"
              className="text-sm text-left border-primary/40 text-primary"
            >
              {ufseq.uf}
              <span className="text-xs font-mono text-slate-400 w-7 text-right flex-shrink-0">
                {ufseq.sequencia}
              </span>
            </Badge>
          ))}
        </span>

        {temEspecificas && (
          <Badge
            variant="outline"
            className="text-xs flex-shrink-0 border-primary/40 text-primary"
          >
            específico
          </Badge>
        )}
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {open
            ? "—"
            : qtd > 0
              ? `${qtd} pergunta${qtd !== 1 ? "s" : ""}`
              : "sem perguntas"}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <PecaEditor
          peca={peca}
          session={session}
          onSaveSuccess={() => {
            setOpen(false);
            setTimeout(() => {
              const next = document.getElementById(`peca-row-${index + 1}`);
              next?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 150);
          }}
        />
      )}
    </div>
  );
}

// ─── Vinculação em lote: selecionar peças + perguntas ─────────────────────

function VincularEmLoteDialog({
  open,
  onClose,
  pecas,
  tipoVeiculo,
  session,
}: {
  open: boolean;
  onClose: () => void;
  pecas: PecaUnica[];
  tipoVeiculo: string;
  session: any;
}) {
  const { toast } = useToast();
  const [pecasSelecionadas, setPecasSelecionadas] = useState<Set<string>>(
    new Set(),
  );
  const [perguntas, setPerguntas] = useState<string[]>([]);
  const [searchPeca, setSearchPeca] = useState("");

  // Reseta ao abrir/fechar
  useEffect(() => {
    if (open) {
      setPecasSelecionadas(new Set());
      setPerguntas([]);
      setSearchPeca("");
    }
  }, [open]);

  const pecasFiltradas = useMemo(() => {
    if (!searchPeca.trim()) return pecas;
    const q = searchPeca.trim().toLowerCase();
    return pecas.filter((p) => p.nome.toLowerCase().includes(q));
  }, [pecas, searchPeca]);

  const togglePeca = (nome: string) => {
    setPecasSelecionadas((prev) => {
      const next = new Set(prev);
      if (next.has(nome)) next.delete(nome);
      else next.add(nome);
      return next;
    });
  };

  const toggleAll = () => {
    if (pecasSelecionadas.size === pecasFiltradas.length) {
      setPecasSelecionadas(new Set());
    } else {
      setPecasSelecionadas(new Set(pecasFiltradas.map((p) => p.nome)));
    }
  };

  const bulkSyncMutation = useMutation({
    mutationFn: async ({
      nomes,
      perguntasTextos,
    }: {
      nomes: string[];
      perguntasTextos: string[];
    }) => {
      const results = [];
      for (const nome of nomes) {
        const res = await fetch(api.pecaPerguntas.sync.path, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ nome, tipoVeiculo, perguntas: perguntasTextos }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(`Erro em "${nome}": ${err.message || "Erro interno"}`);
        }
        results.push(await res.json());
      }
      return results;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [api.pecaPerguntas.list.path],
      });
      toast({
        title: "Perguntas vinculadas",
        description: `${vars.perguntasTextos.length} pergunta(s) aplicadas a ${vars.nomes.length} peça(s).`,
      });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro ao vincular",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleAplicar = () => {
    const nomes = Array.from(pecasSelecionadas);
    if (nomes.length === 0) {
      toast({
        title: "Nenhuma peça selecionada",
        description: "Selecione ao menos uma peça.",
        variant: "destructive",
      });
      return;
    }
    if (perguntas.length === 0) {
      toast({
        title: "Nenhuma pergunta selecionada",
        description: "Selecione ao menos uma pergunta.",
        variant: "destructive",
      });
      return;
    }
    bulkSyncMutation.mutate({ nomes, perguntasTextos: perguntas });
  };

  const allVisibleSelected =
    pecasFiltradas.length > 0 &&
    pecasFiltradas.every((p) => pecasSelecionadas.has(p.nome));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Link2 className="w-5 h-5 text-primary" />
            Vincular Perguntas em Lote
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Selecione as peças e as perguntas que serão vinculadas a elas.
          </p>
        </DialogHeader>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Coluna esquerda: seleção de peças */}
          <div className="md:w-1/2 border-b md:border-b-0 md:border-r flex flex-col min-h-0">
            <div className="px-4 pt-4 pb-2 flex-shrink-0 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  Peças{" "}
                  {pecasSelecionadas.size > 0 && (
                    <span className="text-primary font-semibold">
                      ({pecasSelecionadas.size} selecionada
                      {pecasSelecionadas.size !== 1 ? "s" : ""})
                    </span>
                  )}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAll}
                  className="text-xs h-7"
                >
                  {allVisibleSelected ? "Desmarcar todas" : "Selecionar todas"}
                </Button>
              </div>
              <Input
                value={searchPeca}
                onChange={(e) => setSearchPeca(e.target.value)}
                placeholder="Buscar peça..."
                className="h-8 text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
              {pecasFiltradas.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nenhuma peça encontrada.
                </p>
              ) : (
                pecasFiltradas.map((peca) => (
                  <label
                    key={peca.nome}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer select-none"
                  >
                    <Checkbox
                      checked={pecasSelecionadas.has(peca.nome)}
                      onCheckedChange={() => togglePeca(peca.nome)}
                    />
                    <span className="text-sm text-slate-700 flex-1">
                      {peca.nome}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Coluna direita: seleção de perguntas */}
          <div className="md:w-1/2 flex flex-col min-h-0">
            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <p className="text-sm font-medium text-slate-700">
                Perguntas{" "}
                {perguntas.length > 0 && (
                  <span className="text-primary font-semibold">
                    ({perguntas.length} selecionada
                    {perguntas.length !== 1 ? "s" : ""})
                  </span>
                )}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto pb-4">
              <BancoSelector
                textos={perguntas}
                onChange={setPerguntas}
                session={session}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-shrink-0 gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleAplicar}
            disabled={
              bulkSyncMutation.isPending ||
              pecasSelecionadas.size === 0 ||
              perguntas.length === 0
            }
          >
            {bulkSyncMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            Aplicar a {pecasSelecionadas.size} peça
            {pecasSelecionadas.size !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────

export default function PecaPerguntas() {
  const { data: todasPecas, isLoading } = usePecas();
  const { profile, session } = useAuth();
  const isOwner = profile?.roles?.includes("owner");
  const isAdmin = profile?.roles?.includes("admin");
  const isAllowed = isOwner || isAdmin;
  const [tipoSelecionado, setTipoSelecionado] = useState("auto_leve");
  const [showVincularLote, setShowVincularLote] = useState(false);

  const { data: padrao } = usePerguntasPadrao(tipoSelecionado);
  const qtdPadrao = padrao?.length ?? 0;

  if (!isAllowed) {
    return (
      <Shell>
        <div className="text-center py-16 text-muted-foreground">
          <HelpCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Acesso restrito ao OWNER ou ADMINISTRADOR.</p>
        </div>
      </Shell>
    );
  }

  const pecasUnicas = deduplicarPecas(todasPecas ?? [], tipoSelecionado);

  return (
    <Shell>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display text-slate-900">
            Perguntas por Peça
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure o banco de perguntas e selecione as que se aplicam a cada
            peça
          </p>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList className="mb-6">
          <TabsTrigger value="config">
            <Settings2 className="w-4 h-4 mr-2" />
            Configuração
          </TabsTrigger>
          <TabsTrigger value="banco">
            <Library className="w-4 h-4 mr-2" />
            Banco de Perguntas
          </TabsTrigger>
        </TabsList>

        {/* ── Aba: Configuração por peça ── */}
        <TabsContent value="config">
          <div className="sticky top-0 z-10 bg-background py-2 -mx-1 px-1 flex items-center justify-between mb-4 gap-4">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={
                  VEICULO_TIPO_COLORS[tipoSelecionado] ||
                  "bg-slate-100 text-slate-700"
                }
              >
                {VEICULO_TIPO_LABELS[tipoSelecionado]}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {pecasUnicas.length} peça(s)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVincularLote(true)}
                disabled={pecasUnicas.length === 0}
              >
                <Link2 className="w-4 h-4 mr-1" />
                Vincular em Lote
              </Button>
              <Select value={tipoSelecionado} onValueChange={setTipoSelecionado}>
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VEICULO_TIPO_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Seção de perguntas padrão do tipo */}
          <PerguntasPadraoSection
            tipoVeiculo={tipoSelecionado}
            session={session}
          />

          {/* Lista de peças */}
          {isLoading ? (
            <div className="space-y-2">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded-lg" />
                ))}
            </div>
          ) : pecasUnicas.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <HelpCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                Nenhuma peça cadastrada para este tipo de veículo.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-3">
                Expanda uma peça para selecionar as perguntas específicas
                (substitui o padrão acima para essa peça).
              </p>
              {pecasUnicas.map((peca, i) => (
                <PecaRow
                  key={`${peca.nome}-${peca.tipoVeiculo}`}
                  peca={peca}
                  index={i}
                  session={session}
                  qtdPadrao={qtdPadrao}
                />
              ))}
            </div>
          )}

          <VincularEmLoteDialog
            open={showVincularLote}
            onClose={() => setShowVincularLote(false)}
            pecas={pecasUnicas}
            tipoVeiculo={tipoSelecionado}
            session={session}
          />
        </TabsContent>

        {/* ── Aba: Banco de Perguntas ── */}
        <TabsContent value="banco">
          <BancoGerenciamento session={session} />
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
